import { Building2, Users, ArrowRight, FileCheck2, Cog, Rocket, Network, TrendingUp, ShieldCheck, ScrollText, FileSpreadsheet, Briefcase } from "lucide-react";
import {
  developerGroupTeam,
  developerGroupContract,
  developerGroupTotals,
} from "@/lib/proposalData";
import { useDeployment } from "@/lib/DeploymentContext";
import { generateBudgetSpreadsheet } from "@/lib/spreadsheetGenerator";

const fmt = (n) => `$${n.toLocaleString()}`;
const phaseIcons = [FileCheck2, FileCheck2, Cog, Rocket];

export default function Budget() {
  const d = useDeployment();
  const {
    hubLines: centralHubLines,
    agencyLines: agencyBudgetLines,
    portfolios: portfolioContracts,
    systemTotals,
    fiveYear: fiveYearForecast,
    fiveYearTotal: fiveYearGrandTotal,
    fiveYearAllIn: fiveYearAllInTotal,
    roi: triPortfolioROI,
    operationalPhases,
    badge: budgetBadge,
  } = d.budget;
  return (
    <section
      id="budget"
      data-testid="section-budget"
      className="bg-white py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Proposal Status Disclaimer */}
        <div data-testid="budget-disclaimer" className="mb-8 border-l-4 border-[#D4AF37] bg-[#FEF8E7] px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B6914] whitespace-nowrap mt-0.5">
              ⚠ Proposed Budget
            </span>
            <p className="text-xs text-[#5C4A0A] leading-relaxed">
              <strong>Illustrative budget developed by Sterile Mate LLC for discussion with {d.cityLabel} leadership.</strong>{" "}
              Figures are subject to negotiation and have not been adopted by Council, the Board, or any funding agency. Not a binding offer. Document version: <em>Proposal v1.0</em>.
            </p>
          </div>
        </div>
        {/* Proposal-scope banner */}
        <div
          data-testid="budget-deployment-badge"
          className="mb-8 inline-flex items-center gap-3 bg-[#0A192F] text-[#F9F7F1] px-4 py-2"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#D4AF37]">
            Budget Scope
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest">
            {budgetBadge}
          </span>
        </div>
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                The Tri-Portfolio Framework · 5-Year Multi-Agency Model
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              One {d.hubShort.replace(/^the /, "")}. Three agencies.<br />
              <em className="italic">{systemTotals.householdsServed.toLocaleString()} households a year.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              {d.budgetHubLine} <strong>Hub</strong> —
              while three competitively-selected nonprofit service providers operate as
              <strong> Spokes</strong>, each running a distinct {systemTotals.householdsServed / systemTotals.agencyCount}-household portfolio.
              Five-year contracts with annual performance review per agency. One
              standardized data platform across the entire system.
            </p>
          </div>
        </div>

        {/* Spreadsheet Download CTA */}
        <div data-testid="budget-spreadsheet-cta" className="mb-12 bg-[#0A192F] text-[#F9F7F1] p-5 lg:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-15 pointer-events-none" />
          <div className="relative z-10 flex items-start gap-4">
            <FileSpreadsheet className="h-6 w-6 text-[#D4AF37] shrink-0 mt-1" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                For Council, Finance &amp; RFP Review
              </div>
              <div className="font-display text-lg lg:text-xl leading-tight">
                Download the complete 5-year financials &amp; program goals as an Excel workbook.
              </div>
              <p className="text-xs text-[#CBD5E1] mt-1 max-w-2xl">
                11 sheets: Executive Summary · 5-Year Forecast · Hub Budget · Per-Agency Budget · Developer Group · Tri-Portfolio · Program Goals · Outcome Benchmarks · Launch Plan · ROI · Partners.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="download-spreadsheet-btn"
            onClick={generateBudgetSpreadsheet}
            className="relative z-10 inline-flex items-center justify-center gap-2 bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest whitespace-nowrap transition-colors"
          >
            Download .xlsx <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Headline totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-16">
          {[
            { k: fmt(systemTotals.grandTotal), v: "Total Annual Regional Investment", accent: "#0A192F" },
            { k: systemTotals.householdsServed.toLocaleString(), v: "Households Served Annually", accent: "#0A192F" },
            { k: fmt(systemTotals.costPerHousehold), v: "Cost / Household / Year (~$230/mo)", accent: "#8B0000" },
            { k: `1 : ${systemTotals.caseload}`, v: "Service-Connector Caseload Cap", accent: "#0A192F" },
          ].map((s, i) => (
            <div key={i} data-testid={`budget-headline-${i}`} className="bg-white p-6 lg:p-7">
              <div className="font-display text-3xl lg:text-4xl" style={{ color: s.accent }}>
                {s.k}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-2 leading-snug">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Hub-and-Spokes diagram */}
        <div data-testid="budget-hub-spokes" className="grid lg:grid-cols-12 gap-4 mb-16">
          <div className="lg:col-span-4 bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-8 relative overflow-hidden flex flex-col justify-between min-h-[280px]">
            <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
            <div className="relative z-10">
              <Network className="h-7 w-7 text-[#D4AF37] mb-4" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                The Hub
              </div>
              <div className="font-display text-2xl lg:text-3xl leading-tight">
                {d.budgetHubBlock}<br />Central Coordination Unit
              </div>
            </div>
            <div className="relative z-10 mt-6 pt-5 border-t border-[#F9F7F1]/20">
              <div className="font-display text-3xl text-[#F9F7F1]">{fmt(systemTotals.centralHub)}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1] mt-1">
                Executive team, clinical oversight, field audit
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-3">
            {portfolioContracts.map((p) => (
              <div
                key={p.id}
                data-testid={`portfolio-${p.id}`}
                className="bg-[#F9F7F1] border border-[#CBD5E1] p-5 lg:p-6 flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="grid place-items-center w-10 h-10 border" style={{ borderColor: p.color, color: p.color }}>
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: p.color }}>
                    Spoke 0{p.id}
                  </span>
                </div>
                <h4 className="font-display text-base text-[#0A192F] leading-snug">{p.name}</h4>
                <p className="mt-2 text-xs text-[#475569] leading-relaxed flex-1">{p.focus}</p>
                <div className="mt-4 pt-3 border-t border-[#CBD5E1] flex items-baseline justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#64748B]">
                    {p.households} households
                  </span>
                  <span className="font-display text-lg text-[#0A192F]">{fmt(p.cost)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Operational Phases */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              Multi-Agency Operational Plan · Year-One Launch
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-4xl text-[#0A192F] leading-tight mb-8">
            From Council resolution to {systemTotals.householdsServed.toLocaleString()} households in <em className="italic">six months</em>.
          </h3>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {operationalPhases.map((p, i) => {
              const Icon = phaseIcons[i] || FileCheck2;
              return (
                <li key={p.title} data-testid={`phase-${i}`} className="bg-white p-6 lg:p-7 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="h-6 w-6 text-[#8B0000]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                      {p.period}
                    </span>
                  </div>
                  <h4 className="font-display text-base text-[#0A192F] leading-snug">{p.title}</h4>
                  <p className="mt-3 text-xs text-[#475569] leading-relaxed flex-1">{p.body}</p>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Part A: Central Hub Budget */}
        <div data-testid="budget-central-hub" className="bg-[#F9F7F1] border border-[#CBD5E1] mb-6">
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1] flex items-baseline justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                Part A · The City Hub
              </div>
              <div className="font-display text-xl text-[#0A192F]">
                Central Coordination Unit Infrastructure
              </div>
            </div>
            <div className="font-display text-2xl text-[#0A192F] whitespace-nowrap">
              {fmt(systemTotals.centralHub)}
            </div>
          </div>
          <ul className="divide-y divide-[#CBD5E1]">
            {centralHubLines.map((l, i) => (
              <li key={l.line} data-testid={`hub-line-${i}`} className="px-6 lg:px-8 py-4 grid grid-cols-12 gap-4 items-baseline">
                <div className="col-span-9">
                  <div className="font-display text-base text-[#0A192F]">{l.line}</div>
                  <div className="text-xs text-[#475569] mt-0.5">{l.detail}</div>
                </div>
                <div className="col-span-3 text-right font-display text-base text-[#0A192F]">{fmt(l.cost)}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Part B: Per-Agency Contract */}
        <div data-testid="budget-per-agency" className="bg-[#0A192F] text-[#F9F7F1] mb-16 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10">
            <div className="px-6 lg:px-8 py-5 border-b border-[#F9F7F1]/15 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                  Part B · Per-Agency Contract (× 3 Spokes)
                </div>
                <div className="font-display text-xl">
                  Service Provider Operating Budget · {portfolioContracts[0].households} Households
                </div>
              </div>
              <div className="font-display text-2xl whitespace-nowrap">
                {fmt(systemTotals.perAgency)} <span className="text-[#D4AF37] text-base">/ agency</span>
              </div>
            </div>
            <ul className="divide-y divide-[#F9F7F1]/15">
              {agencyBudgetLines.map((l, i) => (
                <li key={l.role} data-testid={`agency-line-${i}`} className="px-6 lg:px-8 py-4 grid grid-cols-12 gap-4 items-baseline">
                  <div className="col-span-8 lg:col-span-7">
                    <div className="font-display text-base text-[#F9F7F1]">{l.role}</div>
                    <div className="text-xs text-[#CBD5E1] mt-0.5">{l.detail}</div>
                  </div>
                  <div className="hidden lg:block lg:col-span-2 text-center font-mono text-xs text-[#94A3B8]">
                    {l.fte} FTE
                  </div>
                  <div className="col-span-4 lg:col-span-3 text-right font-display text-base text-[#F9F7F1]">{fmt(l.cost)}</div>
                </li>
              ))}
            </ul>
            <div className="px-6 lg:px-8 py-5 border-t border-[#F9F7F1]/15 grid sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">3 Agencies × {fmt(systemTotals.perAgency)}</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">{fmt(systemTotals.totalAgencies)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">+ Central Hub</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">{fmt(systemTotals.centralHub)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Total Annual System</div>
                <div className="font-display text-2xl text-[#D4AF37] mt-1">{fmt(systemTotals.grandTotal)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Part C: Developer Group · Sterile Mate LLC */}
        <div data-testid="budget-developer-group" className="bg-[#F9F7F1] border border-[#CBD5E1] mb-16">
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1] flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                Part C · Sterile Mate Developer Group
              </div>
              <div className="font-display text-xl text-[#0A192F]">
                2-Year Firm Build + 3-Year City Options
              </div>
              <p className="text-xs text-[#475569] mt-1 max-w-2xl leading-relaxed">
                A separate, time-limited consulting engagement to develop curriculum, intake matrices, the data platform, and to train-the-trainer. Designed to make {d.hubLabel} operationally independent of Sterile Mate by Year 3.
              </p>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">2-Year Firm</div>
              <div className="font-display text-2xl text-[#0A192F]">{fmt(developerGroupTotals.twoYearFirm)}</div>
            </div>
          </div>

          {/* Team roster */}
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3 flex items-center gap-2">
              <Briefcase className="h-3.5 w-3.5" /> Year 1 Team Roster
            </div>
            <ul className="divide-y divide-[#CBD5E1]">
              {developerGroupTeam.map((m, i) => (
                <li key={m.role} data-testid={`dev-team-${i}`} className="py-3 grid grid-cols-12 gap-4 items-baseline">
                  <div className="col-span-9">
                    <div className="font-display text-base text-[#0A192F]">{m.role}</div>
                    <div className="text-xs text-[#64748B] mt-0.5"><em>{m.who}</em> · {m.detail}</div>
                  </div>
                  <div className="col-span-3 text-right font-display text-base text-[#0A192F]">{fmt(m.cost)}</div>
                </li>
              ))}
              <li className="py-3 grid grid-cols-12 gap-4 items-baseline border-t border-[#0A192F]/15 mt-2">
                <div className="col-span-9 font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">+ Direct Expenses (curriculum, materials, travel)</div>
                <div className="col-span-3 text-right font-display text-base text-[#0A192F]">{fmt(developerGroupTotals.directExpensesYr1)}</div>
              </li>
              <li className="py-3 grid grid-cols-12 gap-4 items-baseline bg-[#0A192F] text-[#F9F7F1] px-3 -mx-3">
                <div className="col-span-9 font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Year 1 Developer Group Total</div>
                <div className="col-span-3 text-right font-display text-base text-[#D4AF37]">{fmt(developerGroupTotals.year1)}</div>
              </li>
            </ul>
          </div>

          {/* Contract schedule */}
          <div className="px-6 lg:px-8 py-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3">
              Multi-Year Contract Schedule
            </div>
            <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
              {developerGroupContract.map((c, i) => (
                <li
                  key={c.period}
                  data-testid={`dev-contract-${i}`}
                  className={`p-4 flex flex-col ${c.highlight ? "bg-white" : "bg-[#F9F7F1]"}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">{c.period}</div>
                  <div className="font-display text-sm text-[#0A192F] mt-1">{c.phase}</div>
                  <div className="font-display text-lg text-[#0A192F] mt-2">{fmt(c.cost)}</div>
                  <p className="text-[10px] text-[#475569] mt-2 leading-snug flex-1">{c.detail}</p>
                </li>
              ))}
            </ol>
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#64748B] mt-4 leading-relaxed">
              5-Year Maximum (all options exercised): <strong className="text-[#0A192F]">{fmt(developerGroupTotals.fiveYearAllOptions)}</strong> · Years 3–5 are <em>City options, not firm commitments</em>.
            </p>
          </div>
        </div>

        {/* ROI Pitch */}
        <div data-testid="budget-roi" className="bg-[#F9F7F1] border border-[#CBD5E1] p-7 lg:p-10 mb-12">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <TrendingUp className="h-7 w-7 text-[#8B0000] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                The City Council Financial Pitch
              </div>
              <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F] leading-tight">
                Compared to <em className="italic">what?</em>
              </h3>
              <p className="text-sm text-[#475569] mt-4 leading-relaxed">
                The entire system breaks down to roughly{" "}
                <strong className="text-[#0A192F]">$2,756 per household per year</strong> in
                Year 1 — about <strong className="text-[#0A192F]">$230 a month</strong> to
                wrap a family or senior in intensive case management with a fully-
                staffed Central Coordination Unit supervising every agency.
              </p>
            </div>
            <ul className="lg:col-span-8 space-y-4">
              {triPortfolioROI.map((r, i) => (
                <li
                  key={r.label}
                  data-testid={`roi-row-${i}`}
                  className={`pl-5 border-l-2 ${r.highlight ? "border-[#8B0000]" : "border-[#CBD5E1]"}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <span className={`font-display text-lg leading-tight ${r.highlight ? "text-[#8B0000]" : "text-[#0A192F]"}`}>
                      {r.label}
                    </span>
                    <span className="font-mono text-sm text-[#0A192F] whitespace-nowrap">{r.value}</span>
                  </div>
                  <p className="text-xs text-[#475569] mt-1 leading-relaxed">{r.note}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Economic Return narrative */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          <div data-testid="econ-workforce" className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-8">
            <Users className="h-7 w-7 text-[#D4AF37] mb-4" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
              Workforce Generation
            </div>
            <h4 className="font-display text-xl lg:text-2xl mb-3">
              100 {d.college} graduates pay for the program.
            </h4>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              If Agency 3 alone graduates 100 single mothers from {d.college} into
              stable, living-wage industrial, medical, or logistic careers in {d.cityRegion},
              the newly generated local spending and municipal tax base{" "}
              <strong className="text-[#F9F7F1]">easily covers the operational cost</strong>
              {" "}— with surplus.
            </p>
          </div>
          <div data-testid="econ-cost-avoidance" className="bg-[#8B0000] text-[#F9F7F1] p-7 lg:p-8">
            <ShieldCheck className="h-7 w-7 text-[#D4AF37] mb-4" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
              Municipal Cost Avoidance
            </div>
            <h4 className="font-display text-xl lg:text-2xl mb-3">
              500 seniors aging in place cut the EMS bill.
            </h4>
            <p className="text-sm text-[#F9F7F1]/90 leading-relaxed">
              Keeping 500 seniors safely independent in their own homes dramatically
              cuts the {d.hubShort}&apos;s emergency-medical-services dispatch burden and avoids
              public-homelessness crises — line items that already cost {d.cityRegion} more
              than $2,756 per household, every year, with no exit strategy.
            </p>
          </div>
        </div>

        {/* 5-Year Forecast */}
        <div data-testid="budget-5yr-forecast" className="bg-[#F9F7F1] border border-[#CBD5E1] mb-12">
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1] flex items-baseline justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                5-Year Forecast · 3% Annual Cost-of-Living Raise
              </div>
              <div className="font-display text-xl text-[#0A192F]">
                Disciplined growth. Predictable unit economics.
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                5-Year Recurring · All-In
              </div>
              <div className="font-display text-2xl text-[#0A192F]">{fmt(fiveYearGrandTotal)} <span className="text-base text-[#8B0000]">/ {fmt(fiveYearAllInTotal)}</span></div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white border-b border-[#CBD5E1]">
                  <th className="text-left px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Year</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Hub</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Per Agency</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">3 Agencies</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Recurring Total</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Dev Group</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">All-In Total</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">$ / HH</th>
                </tr>
              </thead>
              <tbody>
                {fiveYearForecast.map((y, i) => (
                  <tr
                    key={y.year}
                    data-testid={`forecast-row-${i}`}
                    className={`border-b border-[#E2E8F0] ${i === 0 ? "bg-[#EBE6D8]" : "bg-white"}`}
                  >
                    <td className="px-4 lg:px-6 py-4 font-display text-base text-[#0A192F]">{y.year}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.hub)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.perAgency)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.totalAgencies)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#8B0000]">{fmt(y.system)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.devGroup)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#0A192F]">{fmt(y.systemAllIn)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#475569]">${y.costPerHH.toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="bg-[#0A192F] text-[#F9F7F1]">
                  <td className="px-4 lg:px-6 py-4 font-display text-base">5-Year Total</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#D4AF37]">{fmt(fiveYearGrandTotal)}</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">{fmt(developerGroupTotals.fiveYearAllOptions)}</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#D4AF37]">{fmt(fiveYearAllInTotal)}</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="px-6 lg:px-8 py-4 border-t border-[#CBD5E1] font-mono text-[10px] uppercase tracking-wider text-[#64748B] leading-relaxed">
            Assumptions — 3% annual cost-of-living raise on all salaried positions (Program Architect, System Auditor, LCSW Program Manager, Track Supervisors, Service Connectors, Intake Specialists). Non-salary operating costs (software, evaluation, admin, office ops) held flat for conservatism. Caseload cap maintained at 1:38 across the entire 5-year horizon.
          </p>
        </div>

        {/* Governance & Conflict-of-Interest */}
        <div data-testid="governance-callout" className="bg-[#F9F7F1] border-l-4 border-[#8B0000] p-6 lg:p-8 mb-12">
          <div className="flex items-start gap-4">
            <ScrollText className="h-6 w-6 text-[#8B0000] shrink-0 mt-1" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-2">
                Governance &amp; Conflict-of-Interest Disclosure
              </div>
              <h4 className="font-display text-xl lg:text-2xl text-[#0A192F] leading-tight mb-3">
                The architect of the model does not operate one of the spokes.
              </h4>
              <p className="text-sm text-[#334155] leading-relaxed">
                The Founding Program Architect is awarded as a sole-source City professional-services contract to the program creator, with a written
                <strong className="text-[#0A192F]"> non-compete on all three Spoke RFPs </strong>
                for the full contract term plus a 12-month cool-down. The Architect serves the evaluation committee in an
                <strong className="text-[#0A192F]"> advisory, non-voting </strong>
                capacity. This structure — separating system design from agency operation — is the institutional safeguard that allows the City to engage the model&apos;s creator without compromising the integrity of the competitive RFP process.
              </p>
            </div>
          </div>
        </div>

        {/* Final bottom CTA */}
        <div className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-9 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
              The Five-Year Ask
            </div>
            <div className="font-display text-2xl lg:text-3xl">
              {fmt(systemTotals.grandTotalAllIn)} Year 1 → {fmt(fiveYearAllInTotal)} total over 5 years.
              <span className="text-[#D4AF37] italic"> Annual review per agency.</span>
            </div>
            <p className="text-[#CBD5E1] text-sm mt-2 max-w-2xl">
              Includes Year-1 Sterile Mate Developer Group buildout ({fmt(developerGroupTotals.year1)}) that declines to advisory-only by Year 4. Recurring 5-year cost of the Hub + 3 Agencies: <strong className="text-[#F9F7F1]">{fmt(fiveYearGrandTotal)}</strong>. Standardized data platform, third-party annual evaluation, hard caseload caps at 1:38, LCSW clinical leadership at every agency.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="budget-cta"
            className="inline-flex items-center gap-2 bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest whitespace-nowrap"
          >
            Schedule a Budget Walk-Through <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* Document footer · audit trail */}
        <p data-testid="budget-audit-footer" className="mt-6 font-mono text-[10px] uppercase tracking-widest text-[#64748B] text-center leading-relaxed">
          Document version: <strong className="text-[#0A192F]">Proposal v1.0</strong> · Last revised {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} · Not a binding offer · Subject to negotiation with {d.cityLabel} leadership
        </p>
      </div>
    </section>
  );
}
